import React from 'react';
import { formatDateUrl } from "@lib/format";
import { decode } from "html-entities";
import moment from "moment";
import queryString from "query-string";
import {Grades} from "@lib/constants";

export const groupBy = (input, key) => {
    if (!Array.isArray(input)) return [];
    return input
        ?.reduce((acc, currentValue) => {
            let groupKey = key.split(".").reduce((r, k) => r?.[k], currentValue);
            if (!acc[groupKey]) {
                acc[groupKey] = [];
            }
            acc[groupKey].push(currentValue);
            return acc;
        }, [])
        .sort((a, b) => a - b);
};

export const zeroPad = (num, places) => String(num).padStart(places, "0");

export const isString = (value) => {
    return typeof value === 'string' || value instanceof String;
}

export const NewHOC = (Component, obj) => {
    return class extends React.Component<any, any> {
        render() {
            return <Component {...obj} {...this.props} />;
        }
    };
};

export const uniqueArrayOfObject = (array, keyToBeUnique) => {
    return Object.values(array.reduce((tmp, x) => {
        if (tmp[x[keyToBeUnique]]) return tmp;
        tmp[x[keyToBeUnique]] = x;
        return tmp;
    }, {}));
}

export const checkIsDate = (check) => {
    if (check && (typeof check === "string")) {
        const objRegExp = /^[0-9]{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])/;
        return objRegExp.test(check);
    }
    return check && (typeof check === 'object') && Object.prototype.toString.call(check) === '[object Date]';
};

export const getMostResentReleaseDate = (target: string | null = null) => {
    let date = moment(target).isValid() ? moment(target).toDate() : new Date();
    date.setHours(0, 0, 0, 0);
    while (date.getDay() !== 3) {
        date.setDate(date.getDate() - 1);
    }
    return date;
};

export const getTitleDetailsFromIssue = (issue) => {
    const regex = /(.*) #([0-9]*) - (.*)/g;
    const fullTitle = decode(issue?.title);
    const titleParts = regex.exec(fullTitle);

    if (titleParts) {
        return {
            fullTitle: fullTitle,
            title: titleParts[1],
            number: Number(titleParts[2]),
            variant: titleParts[3],
        };
    }

    return {
        fullTitle: fullTitle,
        title: fullTitle,
        number: 0,
        variant: fullTitle,
    }
};

export const wrapWordTag = (
    word: string,
    words: string,
    className: string = 'highlight',
    tag: string = 'span',
) => {
    const pattern = new RegExp(`(?<!\\bhttps?:\\/\\/\\S*)\\b(${word})\\b(?!<\\/span>)`, "gi");
    return words?.replace(pattern, (match) => {
        return `<${tag} class="${className}">${match}</${tag}>`;
    });
}

export const wrapWordTagRange = (
    word: string,
    words: string,
    className: string = 'highlight',
    tag: string = 'span',
    range: any = [25, 25],
) => {
    const [before, after] = range;
    const regexTags = /(<([^>]+)>)/ig;
    const wordsRemoveTags = words?.replace(regexTags, "");
    const pattern = RegExp(`\\b(\\w+\\W+){0,${before}}${word}(\\W+\\w+){0,${after}}\\b`, "i");
    const matchs = wordsRemoveTags?.match(pattern);

    if (matchs) {
        const [match] = matchs;
        const results = match?.replace(new RegExp(word, "i"), (match) => {
            return `<${tag} class="${className}">${match}</${tag}>`;
        });
        return `... ${results} ...`;
    } else {
        return words?.replace(regexTags, "");
    }
}

export const delay = async (ms) => {
    return new Promise(res => setTimeout(res, ms));
}

export const scrollToThen = async (scrollOptions, onScrollEnd) => {
    window.scrollTo(scrollOptions);
    if (scrollOptions.behavior === 'smooth') {
        await delay(300);
    } else {
        await delay(1);
    }
    onScrollEnd();
}

export const scrollToTop = () => {
    window.scrollTo({ top: 0 });
};

export const getIsIssueNumberInQueryString = (query: string) => {
    var pattern = new RegExp("#[^\s]+", "gm");
    let found = pattern.exec(query);
    let isolatedIssueNumber = found?.[0].replace('#', '').split(' ')[0];
    if (!isolatedIssueNumber || isolatedIssueNumber === '') {
        return false;
    }
    return true;
};

export const transformEra = (era) => ({
    id: era.id,
    start: era.start_date,
    end: era.end_date
});

const start = (new Date()).getTime();
export const logTime = (...args) => {
    const now = (((new Date()).getTime() - start) / 1000).toFixed(2);
    console.log(...[now, ...args]);
};

export const stopClickEvent = (e) => {
    e.stopPropagation();
    e.preventDefault();
};

export const removeRegexPattern = (value) => {
    const regexPattern = /[.*+?^${}()|[\]\\]/g;

    if (regexPattern.test(value)) {
        value = value.replace(regexPattern, '');
    }

    return value;
};

export const sanitizeSearchInput = (value) => {
    let results = removeRegexPattern(value);
    results = results?.replace(/'/g, "''");
    return results;  // Escape single quotes
}

export const stripTags = (string) => {
    return string?.replace(/<(.|\n)*?>/g, '');
};


export const doesUrlMatchPattern = (url: string, pattern: string): boolean => {

    let regx = /^\/|\/$/g;

    let pathUrl = url
        .replace(regx, '')
        .split('/');

    let pathPattern = pattern
        .replace(regx, '')
        .split('/');


    if (pathUrl.length !== pathPattern.length) {
        return false;
    }

    let pos = pathPattern.map((str, i) => {
        return str.includes(':') ? i : undefined;
    }).filter((pos) => pos !== undefined);

    pos.forEach((p: any) => {
        pathUrl.splice(p, 1);
        pathPattern.splice(p, 1);
    });

    let pathUrlJoined = pathUrl.join('/');
    let pathPatternJoined = pathPattern.join('/');

    return pathUrlJoined === pathPatternJoined;
}

export const findIntersection = (array1, array2) => {
    return array1.filter((value) => array2.includes(value));
}
export const deleteAttr = (obj, values:any=[]) => {
    let deleteWhen = ['undefined', null, ...values];
    for (let key in obj) {
        if (deleteWhen.includes(obj[key])) {
            delete obj[key];
        }
    }
    return obj;
}

export const stringToComponent = (htmlString) => {
    return <div dangerouslySetInnerHTML={{ __html: htmlString }} />;
};


export const encodeBase64URI = (obj) => {
    return btoa(JSON.stringify(obj));
}

export const decodeBase64URI = (str) => {
    return JSON.parse(atob(str.trim()));
}

export const isBase64 = (str: string): boolean => {
    try {
        const decodedString = atob(str);
        const reEncodedString = btoa(decodedString);
        return reEncodedString === str;
    } catch (error) {
        return false;
    }
}

export const transformParamsToQuerystring = (params) => {
    // using queryString.stringify with the filters object caused errors, it added chars to the end of the json object that cuased it to be malformed.
    const filters = params ? encodeBase64URI(params.filters) : '';
    let paramsNoFilters = {...params, filters: undefined};
    delete paramsNoFilters.filters;
    const query = queryString.stringify({...paramsNoFilters}, { arrayFormat: 'comma'}) + `&filters=${filters}`;
    return query;
}

export const getRawBucketByGrade = (grade) => {
    const numGrade = Number(grade);
    let rawBucket;
    if(isNaN(numGrade) && grade){ //is string
        rawBucket = Grades.raw.find((raw) => grade === raw.short_desc || grade === raw.long_desc);
    }else{ //is number
        rawBucket = Grades.raw.find((raw) => numGrade >= raw.low && numGrade <= raw.high);
    }
    return rawBucket;
}

export const blobToData = (blob: Blob, isImage:boolean=false, maxSize:number=2000) => {
    return new Promise((resolve) => {
        const reader = new FileReader()

        if(!isImage) reader.onloadend = () => resolve(reader.result);

        if(isImage) reader.onload = (readerEvent) => {
            const image = new Image();
            image.onload = (imageEvent) => {
                const canvas = document.createElement('canvas');
                let width = image.width;
                let height = image.height;

                if (width > height) {
                    if (width > maxSize) {
                        height *= maxSize / width;
                        width = maxSize;
                    }
                } else {
                    if (height > maxSize) {
                        width *= maxSize / height;
                        height = maxSize;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if(ctx) ctx.drawImage(image, 0, 0, width, height);

                const dataUrl = canvas.toDataURL();
                return resolve(dataUrl);
            }
            if(readerEvent.target?.result) image.src = readerEvent.target.result as any;
        }

        reader.readAsDataURL(blob)
    });
}